import { Injectable, EventEmitter } from '@angular/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

import { ButtonClick } from '../se-ui-controls/se-modalwindow/se-modalwindow.component';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';

export interface INotificationService {

    success(message: string, title?: string);
    warning(message: string, title?: string);
    error(message: string, title?: string);
}

export enum NotificationType {

    information = 0,
    warning = 1,
    error = 2
}

export interface INotification {

    title: string;
    message: string;
    type: NotificationType
}

export interface IVNextNotification extends INotification {
    time: string;
    timeShown: number;
    toBeRemoved: boolean;
    wasShown: boolean;
    count?: number;
}

export interface IModalDialogButtonExecutor {

    buttonClick: EventEmitter<ButtonClick>;
}

export interface IModalDialogNotification extends INotification {
    messageListData?: string[];
    buttonOk: boolean;
    buttonCancel: boolean;
    buttonYes: boolean;
    buttonNo: boolean;
}

class NotificationServiceBase {

    constructor(protected translate: TranslateService) {
    }

    protected getDefaultMessage(type: NotificationType): string {
        switch (type) {
            case NotificationType.error:
                return this.translate.instant('Error');
            case NotificationType.warning:
                return this.translate.instant('Warning');
            case NotificationType.information:
                return this.translate.instant('Information');
        }

        return '';
    }
}

@Injectable({
    providedIn: 'root',
})
export class NotificationService extends NotificationServiceBase implements INotificationService {

    // data members

    notification: EventEmitter<INotification> = new EventEmitter<INotification>();

    // constructor

    vnextNotifications: IVNextNotification[] = [];

    constructor(translate: TranslateService) {
        super(translate);
    }

    // public functions

    notify(notification: INotification) {
        this.notification.emit(notification);
    }

    info(message: string, title?: string) {
        if (!title)
            title = this.getDefaultMessage(NotificationType.information);

        this.success(message, title);
    }

    success(message: string, title?: string) {
        if (!title)
            title = this.translate.instant('SuccessOperation');

        this.message(message, title, NotificationType.information);
    }

    warning(message: string, title?: string) {
        if (!title)
            title = this.getDefaultMessage(NotificationType.warning);

        this.message(message, title, NotificationType.warning);
    }

    error(message: string, title?: string) {
        if (!title)
            title = this.getDefaultMessage(NotificationType.error);

	this.message(message, title, NotificationType.error);
    }

    /*
     * Gestisce le risposte http che hanno estensa fault  (eg: quelle del wado)
     * */
    httpError(e: HttpErrorResponse, title: string = undefined, defaultMessage: string = this.translate.instant('GenericError')) {
        let message: string;

        try {
            message = e?.error?.estensaFaultDetails?.estensaFault?.message;
            if (!message)
                message = e.message;
        }
        catch{
            message = defaultMessage;
        }

        this.message(message, title, NotificationType.error);
    }

    // Risposte vecchio client
    httpEstensaFaultError(e, title: string = undefined, defaultMessage: string = this.translate.instant('GenericError')) {
        let message: string;

        try {
            message = e?.data?.exceptionMessage;
	    if(!message)
	    	message = defaultMessage;
        }
        catch{
            message = defaultMessage;
        }

        this.message(message, title, NotificationType.error);
    }

    private message(message: string, title?: string, type?: NotificationType) {
        let notificationType = (type == null) ? NotificationType.information : type;

        let notificationTitle = title;
        if (!notificationTitle) {
            switch (notificationType) {
                case NotificationType.error:
                    notificationTitle = this.translate.instant('Error');
                    break;
                case NotificationType.warning:
                    notificationTitle = this.translate.instant('Warning');
                    break;
                case NotificationType.information:
                    notificationTitle = this.translate.instant('Information');
                    break;
                default:
                    notificationTitle = '';
                    break;
            }
        }

        this.notification.emit({ message: message, title: notificationTitle, type: notificationType });
    }
}

@Injectable({
    providedIn: 'root',
})
export class ModalDialogNotificationService extends NotificationServiceBase implements INotificationService {

    // data members

    notification: EventEmitter<IModalDialogNotification> = new EventEmitter<IModalDialogNotification>();
    buttonClick: EventEmitter<ButtonClick> = new EventEmitter<ButtonClick>();

    private _modalDialogButtonExecutor: IModalDialogButtonExecutor;
    private _buttonClickSubscriber;

    // constructor

    constructor(translate: TranslateService) {
        super(translate);
    }

    // public functions

    register(modalDialogButtonExecutor: IModalDialogButtonExecutor) {
        this._modalDialogButtonExecutor = modalDialogButtonExecutor;
        this._buttonClickSubscriber = this._modalDialogButtonExecutor.buttonClick.subscribe((button) => {
            this.buttonClick.emit(button);
        });
    }

    unregister() {
        this._buttonClickSubscriber.unsubscribe();
        this._modalDialogButtonExecutor.buttonClick.unsubscribe();
    }

    notify(notification: IModalDialogNotification): Observable<ButtonClick> {
        this.notification.emit(notification);
        return this.buttonClick.pipe(take(1));
    }

    info(message: string, title?: string) {
        if (!title)
            title = this.getDefaultMessage(NotificationType.information);

        this.message(message, title, NotificationType.information);
    }

    success(message: string, title?: string) {
        if (!title)
            title = this.translate.instant('SuccessOperation');

        return this.message(message, title, NotificationType.information);
    }

    warning(message: string, title?: string) {
        if (!title)
            title = this.getDefaultMessage(NotificationType.warning);

        return this.message(message, title, NotificationType.warning);
    }

    error(message: string, title?: string) {
        if (!title)
            title = this.getDefaultMessage(NotificationType.error);

        return this.message(message, title, NotificationType.error);
    }

    private message(message: string, title?: string, type?: NotificationType) {

        return this.notify({
            message: message,
            title: title,
            type: type == null ? NotificationType.information : type,
            buttonOk: true,
            buttonCancel: false,
            buttonYes: false,
            buttonNo: false
        });
    }
}
